import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { ApiError } from '../lib/api'
import {
  type InvitationPreview,
  useAcceptInvitation,
  useDeclineInvitation,
  useInvitation,
} from '../lib/api/invitations'
import { useAuth } from '../lib/auth'

export const Route = createFileRoute('/accept-invite/$token')({
  component: AcceptInvitePage,
})

/**
 * Invitation acceptance page.
 *
 *   1. Fetch invitation preview (public endpoint).
 *   2. If unsigned → render sign-in prompt with the email pre-filled and
 *      locked. The /login page threads the invite token through to
 *      /auth/callback so we re-land here after sign-in.
 *   3. If signed in but the session email differs from the invitation —
 *      surface the mismatch and offer sign-out.
 *   4. Otherwise show details + Accept / Decline.
 */
function AcceptInvitePage() {
  const { token } = Route.useParams()
  const { session } = useAuth()
  const navigate = useNavigate()

  const { data: invitation, error: fetchError, isLoading } = useInvitation(token)

  if (isLoading) {
    return (
      <Card className="w-full max-w-[480px] mx-auto my-8 sm:my-16" style={{ maxWidth: 480 }}>
        <p style={{ color: '#6b7280', marginTop: 0 }}>Loading invitation...</p>
      </Card>
    )
  }

  if (fetchError) {
    return <InvitationErrorCard error={fetchError} />
  }
  if (!invitation) {
    return <InvitationErrorCard error={new Error('Invitation not found')} />
  }

  // Unauthenticated — bounce to /login with the invite + email threaded so
  // the magic-link callback round-trips us back here.
  if (!session) {
    return <SignInWithInvite invitation={invitation} token={token} />
  }

  // Signed-in but with the wrong email.
  if (
    typeof session.user.email === 'string' &&
    session.user.email.toLowerCase() !== invitation.email.toLowerCase()
  ) {
    return <EmailMismatchCard invitation={invitation} />
  }

  return (
    <AcceptCard
      invitation={invitation}
      token={token}
      onAccepted={(redirect) => {
        void navigate({ to: redirect, replace: true })
      }}
      onDeclined={() => {
        void navigate({ to: '/' })
      }}
    />
  )
}

// ---------------------------------------------------------------------------

function InvitationErrorCard({ error }: { error: unknown }) {
  const code = error instanceof ApiError ? error.code : 'unknown'
  let title = 'This invitation could not be loaded'
  let body = error instanceof Error ? error.message : 'Try the link in your email again.'
  if (code === 'invitation_expired') {
    title = 'This invitation has expired'
    body = 'Ask the inviter to send a fresh link.'
  } else if (code === 'invitation_already_used') {
    title = 'This invitation has already been accepted'
    body = 'If that was you, sign in normally to access the org.'
  } else if (code === 'not_found') {
    title = 'Invitation link is invalid'
    body = 'The link may have been mistyped. Ask for a fresh one.'
  }
  return (
    <Card className="w-full max-w-[480px] mx-auto my-8 sm:my-16" style={{ maxWidth: 480 }}>
      <h1 style={{ marginTop: 0 }}>{title}</h1>
      <p style={{ color: '#4b5563' }}>{body}</p>
    </Card>
  )
}

// ---------------------------------------------------------------------------

function SignInWithInvite({
  invitation,
  token,
}: {
  invitation: InvitationPreview
  token: string
}) {
  const navigate = useNavigate()
  return (
    <Card className="w-full max-w-[480px] mx-auto my-8 sm:my-16" style={{ maxWidth: 480 }}>
      <h1 style={{ marginTop: 0 }}>You're invited to {invitation.organization.name}</h1>
      <p style={{ color: '#4b5563' }}>
        You've been invited to join as <strong>{invitation.role}</strong>
        {invitation.character ? (
          <>
            {' '}
            for <strong>{invitation.character.display_name}</strong>
          </>
        ) : null}
        . Sign in with <strong>{invitation.email}</strong> to continue.
      </p>
      <Button
        className="w-full sm:w-auto"
        onClick={() =>
          navigate({
            to: '/login',
            search: { invite: token, email: invitation.email },
          })
        }
      >
        Sign in to accept
      </Button>
    </Card>
  )
}

function EmailMismatchCard({ invitation }: { invitation: InvitationPreview }) {
  const { signOut } = useAuth()
  return (
    <Card className="w-full max-w-[480px] mx-auto my-8 sm:my-16" style={{ maxWidth: 480 }}>
      <h1 style={{ marginTop: 0 }}>Wrong account</h1>
      <p style={{ color: '#b91c1c' }}>
        This invitation is for <strong>{invitation.email}</strong>. Sign out and use that email to
        accept.
      </p>
      <Button
        variant="secondary"
        className="w-full sm:w-auto"
        onClick={async () => {
          await signOut()
        }}
      >
        Sign out
      </Button>
    </Card>
  )
}

function AcceptCard({
  invitation,
  token,
  onAccepted,
  onDeclined,
}: {
  invitation: InvitationPreview
  token: string
  onAccepted: (redirect: string) => void
  onDeclined: () => void
}) {
  const accept = useAcceptInvitation()
  const decline = useDeclineInvitation()
  const [actionError, setActionError] = useState<string | null>(null)

  return (
    <Card className="w-full max-w-[480px] mx-auto my-8 sm:my-16" style={{ maxWidth: 480 }}>
      <h1 style={{ marginTop: 0 }}>You're invited</h1>
      <p style={{ color: '#4b5563' }}>
        You've been invited to join <strong>{invitation.organization.name}</strong> as{' '}
        <strong>{invitation.role}</strong>
        {invitation.character ? (
          <>
            {' '}
            for <strong>{invitation.character.display_name}</strong>
          </>
        ) : null}
        .
      </p>
      {actionError ? (
        <p style={{ color: '#b91c1c', fontSize: 13, marginTop: 4 }}>{actionError}</p>
      ) : null}
      <div className="flex flex-col sm:flex-row gap-2 mt-4">
        <Button
          className="w-full sm:w-auto"
          disabled={accept.isPending || decline.isPending}
          onClick={async () => {
            setActionError(null)
            try {
              const res = await accept.mutateAsync(token)
              onAccepted(res.redirect_to ?? '/')
            } catch (err) {
              setActionError(err instanceof Error ? err.message : 'Failed to accept invitation')
            }
          }}
        >
          {accept.isPending ? 'Accepting...' : 'Accept invitation'}
        </Button>
        <Button
          variant="ghost"
          className="w-full sm:w-auto"
          disabled={accept.isPending || decline.isPending}
          onClick={async () => {
            setActionError(null)
            try {
              await decline.mutateAsync(token)
              onDeclined()
            } catch (err) {
              setActionError(err instanceof Error ? err.message : 'Failed to decline')
            }
          }}
        >
          Decline
        </Button>
      </div>
    </Card>
  )
}
